import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, ScanLine, CalendarClock, FolderArchive, Users, ListChecks, FileSignature, MessageCircle, Crown, Settings, BarChart3, LogOut, Menu, X, Gift, Radar, ShieldAlert, MapPin, Mail, HeartHandshake, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { VerifyBanner } from "@/components/VerifyBanner";
import { useAuth } from "@/context/AuthContext";
import { getPending } from "@/lib/pendingDoc";

const NAV = [
  { to: "/app", label: "Panoramica", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/app/avvisi", label: "Avvisi", icon: Bell, id: "alerts" },
  { to: "/app/analizza", label: "Scatta e analizza", icon: ScanLine, id: "analyze" },
  { to: "/app/email", label: "Email in arrivo", icon: Mail, id: "inbox" },
  { to: "/app/scadenze", label: "Scadenzario", icon: CalendarClock, id: "deadlines" },
  { to: "/app/radar", label: "Radar bonus e calendario", icon: Radar, id: "radar" },
  { to: "/app/archivio", label: "Archivio", icon: FolderArchive, id: "archive" },
  { to: "/app/famiglia", label: "Famiglia", icon: Users, id: "family" },
  { to: "/app/guide", label: "Cosa faccio ora?", icon: ListChecks, id: "guides" },
  { to: "/app/lettere", label: "Lettere e PEC", icon: FileSignature, id: "letters" },
  { to: "/app/chat", label: "Chiedi a Burocrazy", icon: MessageCircle, id: "chat" },
  { to: "/app/truffe", label: "Anti-truffa", icon: ShieldAlert, id: "scam" },
  { to: "/app/caf", label: "CAF vicini", icon: MapPin, id: "caf" },
  { to: "/app/pro", label: "Piano Pro", icon: Crown, id: "pro" },
  { to: "/app/invita", label: "Invita amici", icon: Gift, id: "referral" },
  { to: "/app/impostazioni", label: "Impostazioni e privacy", icon: Settings, id: "settings" },
];

const SideNav = ({ isAdmin, onNav }) => (
  <nav className="flex flex-col gap-0.5">
    {[...NAV, ...(isAdmin ? [{ to: "/app/admin", label: "Admin", icon: BarChart3, id: "admin" }] : [])].map(({ to, label, icon: Icon, end, id }) => (
      <NavLink key={to} to={to} end={end} onClick={onNav} data-testid={`nav-${id}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors duration-200 ${isActive ? "bg-[#1E3A8A] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
        <Icon className="h-4 w-4" />
        {label}
      </NavLink>
    ))}
  </nav>
);

export const AppLayout = () => {
  const { user, logout, refresh } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => {
    refresh();
  }, [pathname, refresh]);
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [simple, setSimple] = useState(null);
  useEffect(() => {
    api.get("/simple/status").then(({ data }) => setSimple(data)).catch(() => {});
    getPending().then((f) => f && navigate("/app/analizza", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex">
      <aside className={`fixed inset-y-0 left-0 z-40 w-72 border-r bg-[#F9F9F6] p-5 flex flex-col overflow-y-auto transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
        <div className="flex items-center justify-between mb-6">
          <Logo to="/app" />
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close-btn"><X className="h-5 w-5" /></button>
        </div>
        <SideNav isAdmin={user?.role === "admin"} onNav={() => setOpen(false)} />
        <Link to="/semplice" className="mt-4 flex items-center gap-3 rounded-xl px-3 py-2 text-sm border border-[#E07A5F]/40 bg-[#E07A5F]/10 text-[#b5533a]" data-testid="nav-simple"><HeartHandshake className="h-4 w-4" /> Modalità semplice</Link>
        <div className="mt-auto pt-6 border-t flex items-center gap-3">
          {user?.picture ? <img src={user.picture} alt="" className="h-9 w-9 rounded-full" /> : <div className="h-9 w-9 rounded-full bg-slate-200" />}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-xs text-slate-500" data-testid="sidebar-user-plan">{user?.plan === "pro" ? (user.pro_source === "gift" ? "Piano Pro (regalo)" : "Piano Pro") : "Piano Free"}</p>
          </div>
          <button onClick={doLogout} className="p-2 rounded-lg hover:bg-slate-100" title="Esci" data-testid="logout-btn"><LogOut className="h-4 w-4" /></button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 lg:ml-72 min-w-0">
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/70 border-b px-4 md:px-8 h-16 flex items-center justify-between">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="sidebar-open-btn"><Menu className="h-5 w-5" /></button>
          <p className="hidden lg:block text-sm text-slate-500">Ciao {user?.name?.split(" ")[0]}, un passo alla volta.</p>
          <NotificationBell />
        </header>
        <main className="p-4 md:p-8 max-w-7xl">
          <VerifyBanner />
          {(simple?.pending?.length > 0 || simple?.assisted) && (
            <Link to="/semplice" className="mb-6 flex items-center gap-3 rounded-2xl border border-[#E07A5F]/40 bg-[#E07A5F]/10 p-4 text-sm" data-testid="simple-banner">
              <HeartHandshake className="h-5 w-5 text-[#E07A5F]" />
              {simple.pending?.length > 0 ? `${simple.pending[0].from} vuole aiutarti con le pratiche: apri la Modalità semplice per accettare.` : "Apri la Modalità semplice: un pulsante per fotografare le lettere e uno per parlare."}
            </Link>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
};
